    package com.partejar.app.feature.gestantes.presentation

import com.partejar.app.core.format.formatBrazilianDate
import com.partejar.app.core.format.parseBrazilianDate
import com.partejar.app.core.format.parseClockTime
import com.partejar.app.core.ui.components.PartejarStatusBadgeTone
import com.partejar.app.feature.gestantes.data.*
import kotlinx.datetime.LocalDateTime

internal data class ExamSummary(val id: Long, val name: String, val date: String, val status: GestanteExamStatus, val resultsLabel: String)

internal fun examSummaries(exams: List<GestanteExam>): List<ExamSummary> = exams
    .sortedByDescending { runCatching { LocalDateTime.parse(it.performedAt) }.getOrNull() }
    .map { ExamSummary(it.id, it.type.name.replace('_', ' '), examDate(it.performedAt), it.status, examResultsLabel(it.results.size)) }

internal fun examCountLabel(count: Int) = if (count == 1) "1 exame registrado" else "$count exames registrados"
internal fun examResultsLabel(count: Int) = when (count) {
    0 -> "Sem resultados registrados"
    1 -> "1 resultado disponível"
    else -> "$count resultados disponíveis"
}

internal fun examDate(value: String): String = formatBrazilianDate(value.substringBefore('T')) ?: "Data não informada"

internal fun examDateTimeInput(date: String, time: String): String? {
    val parsedDate = parseBrazilianDate(date) ?: return null
    val parsedTime = parseClockTime(time) ?: return null
    return LocalDateTime(parsedDate, parsedTime).toString()
}

internal data class ExamHistoryPoint(val examId: Long, val dateTime: LocalDateTime, val value: Double, val displayValue: String)

internal enum class ExamEvolutionState { NOT_APPLICABLE, AWAITING_HISTORY, AVAILABLE }

/** Discoverability is independent from eligibility to draw a comparison. Text is never graphed. */
internal fun examEvolutionState(result: GestanteExamResult, history: List<ExamHistoryPoint>): ExamEvolutionState = when {
    result.freeResult -> ExamEvolutionState.NOT_APPLICABLE
    result.analysisId != null && (!result.evolutionEnabled || result.analysisUnitId == null) -> ExamEvolutionState.NOT_APPLICABLE
    result.parameter.isBlank() || numericExamValue(result.value) == null -> ExamEvolutionState.NOT_APPLICABLE
    history.isEmpty() -> ExamEvolutionState.AWAITING_HISTORY
    else -> ExamEvolutionState.AVAILABLE
}

// Catalog and legacy series never mix. Legacy unit case is significant; no conversions or fuzzy matching.
internal fun comparableExamHistory(exams: List<GestanteExam>, selected: GestanteExam, result: GestanteExamResult): List<ExamHistoryPoint> {
    if (result.freeResult || numericExamValue(result.value) == null) return emptyList()
    val cataloged = result.analysisId != null
    if (cataloged && (!result.evolutionEnabled || result.analysisUnitId == null)) return emptyList()
    val unit = result.unit?.trim()?.takeIf { it.isNotEmpty() }
    val parameter = normalizedExamParameter(result.parameter)
    if (!cataloged && (unit == null || parameter.isEmpty())) return emptyList()
    val points = exams.filter { it.type.id == selected.type.id }.mapNotNull { exam ->
        val matches = exam.results.filter {
            !it.freeResult && if (cataloged) {
                it.analysisId == result.analysisId && it.analysisUnitId == result.analysisUnitId && it.evolutionEnabled
            } else {
                it.analysisId == null && normalizedExamParameter(it.parameter) == parameter && it.unit?.trim() == unit
            }
        }
        // Ambiguous repeated metrics in one exam cannot be ordered or identified reliably.
        val match = matches.singleOrNull() ?: return@mapNotNull null
        val value = numericExamValue(match.value) ?: return@mapNotNull null
        val date = runCatching { LocalDateTime.parse(exam.performedAt) }.getOrNull() ?: return@mapNotNull null
        ExamHistoryPoint(exam.id, date, value, numericExamDisplay(match.value))
    }.distinctBy { it.examId }.sortedBy { it.dateTime }
    return points.takeIf { it.size > 1 && it.any { point -> point.examId == selected.id } && it.map { point -> point.dateTime }.distinct().size > 1 }.orEmpty()
}

private fun normalizedExamParameter(value: String) = value.trim().lowercase().replace(Regex("\\s+"), " ")

internal fun numericExamValue(value: String): Double? {
    val input = value.trim()
    if (!Regex("[+-]?[0-9]+([.,][0-9]+)?").matches(input)) return null
    return input.replace(',', '.').toDoubleOrNull()?.takeIf { it.isFinite() }
}

internal fun numericExamDisplay(value: String): String =
    if (numericExamValue(value) != null) value.trim().replace('.', ',') else value

internal fun GestanteExamStatus.toExamLabel(): String = when (this) {
    GestanteExamStatus.PENDING -> "Pendente"
    GestanteExamStatus.NORMAL -> "Normal"
    GestanteExamStatus.ATTENTION -> "Atenção"
    GestanteExamStatus.ALTERED -> "Alterado"
}

internal fun GestanteExamStatus.toBadgeTone(): PartejarStatusBadgeTone = when (this) {
    GestanteExamStatus.PENDING -> PartejarStatusBadgeTone.INFO
    GestanteExamStatus.NORMAL -> PartejarStatusBadgeTone.SUCCESS
    GestanteExamStatus.ATTENTION -> PartejarStatusBadgeTone.ATTENTION
    GestanteExamStatus.ALTERED -> PartejarStatusBadgeTone.ERROR
}
